import { CardTable, SORT } from 'common/components/datatable/Table'
import React, { useMemo, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { getDuration, getLevelColor } from 'supervision/timeErrors/utils/TimeErrorUtils'
import { getDate, getFullDate, shortenHumanize } from 'common/utils/DateUtil'
import { Card, CardContent, Dialog, DialogContent } from '@mui/material'
import PropTypes from 'prop-types'
import useBoolean from 'common/hooks/useBoolean'
import { BasicDialogTitle } from 'common/components/modal/DialogComponents'
import ReactECharts from 'echarts-for-react'
import { maxBy, minBy, orderBy } from 'lodash'
import ApplicationConf from 'conf/ApplicationConf'
import useFetch from 'common/hooks/useFetch'
import useUpdateEffect from 'common/hooks/useUpdateEffect'
import { STATUS } from 'common/constants/storeConstants'

const Graph = ({
    timeErrors = [],
}) => {
    const interval = timeErrors.length ? maxBy(timeErrors, 'requestStart')?.requestStart - minBy(timeErrors, 'requestStart')?.requestStart : 0
    const options = {
        series: [{
            type: 'line',
            data: orderBy(timeErrors, 'requestStart').map(te => [te.requestStart, getDuration(te)]),
            smooth: true,
        }],
        xAxis: [{
            type: 'time',
            axisLabel: {
                formatter: v => getDate(v),
            },
            splitLine: { show: true },
            axisLine: { show: true },
            axisTick: { show: true },
            min: ({ min }) => min - interval * 0.05,
            max: ({ max }) => max + interval * 0.05,
        }],
        yAxis: [{
            type: 'time',
            axisLabel: {
                formatter: shortenHumanize,
            },
            axisLine: { show: true },
            axisTick: { show: true },
            splitLine: { show: true },
            min: 0,
            max: ({ max }) => max * 1.05,
        }],
        grid: { top: 20, right: 20, bottom: 20, left: 60, height: 360 },
        tooltip: {
            trigger: 'axis',
            formatter: params => {
                const {
                    marker,
                    data: [date, duration],
                } = params[0]
                return `${getFullDate(date)}<br />${marker}${shortenHumanize(duration)}`
            },
        },
    }
    return (
        <ReactECharts
            option={options}
            notMerge={true}
            lazyUpdate={true}
            style={{ height: 400 }}
        />
    )
}

Graph.propTypes = {
    timeErrors: PropTypes.arrayOf(PropTypes.shape({
        routingKey: PropTypes.string,
        url: PropTypes.string,
        instance: PropTypes.number,
        service: PropTypes.number,
        level: PropTypes.number,
        requestStart: PropTypes.number,
        processStart: PropTypes.number,
        processEnd: PropTypes.number,
        user: PropTypes.string,
        module: PropTypes.string,
        referer: PropTypes.string,
        userAgent: PropTypes.string,
        body: PropTypes.string,
        size: PropTypes.string,
    })),
}

const PopinGraphTimeErrors = ({
    isOpen = false,
    onClose = () => { },
    routingKey = '',
    instance = 0,
}) => {
    const {
        instances,
    } = useSelector(store => ({
        instances: store.supervision.instances,
    }), shallowEqual)

    const {
        data: timeErrors = [],
        reload,
        reset,
    } = useFetch(ApplicationConf.timeError.list(), { method: 'POST', body: { routingKey, instance } })

    useUpdateEffect(() => {
        if (!isOpen) {
            reset()
            return
        }
        reload()
    }, [isOpen])

    return (
        <Dialog
            maxWidth='lg'
            open={isOpen}
            onClose={onClose}
        >
            <BasicDialogTitle onClose={onClose}>
                {`${instances.byId[instance]?.name}: ${routingKey}`}
            </BasicDialogTitle>
            <DialogContent style={{ overflowX: 'hidden' }}>
                <Card>
                    <CardContent>
                        <Graph timeErrors={timeErrors} />
                    </CardContent>
                </Card>
            </DialogContent>
        </Dialog>
    )
}

PopinGraphTimeErrors.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    routingKey: PropTypes.string,
    instance: PropTypes.number,
}

const TABLE_HEADERS = [
    { key: 'routingKey', value: 'Routing key' },
    { key: 'url', value: 'URL' },
    { key: 'instance', value: 'Instance' },
    { key: 'service', value: 'Service' },
    { key: 'requestStart', value: 'Début de la requête' },
    { key: 'processStart', value: 'Début du traitement' },
    { key: 'processEnd', value: 'Fin du traitement' },
    { key: 'duration', value: 'Duré' },
    { key: 'user', value: 'utilisateur' },
    { key: 'module', value: 'module' },
]

const TimeErrorsTable = ({
    timeErrors = [],
    reload = () => { },
    status,
}) => {
    const {
        services,
        instances,
    } = useSelector(store => ({
        services: store.supervision.services,
        instances: store.supervision.instances,
    }), shallowEqual)

    const [selected, setSelected] = useState({})
    const {
        value: isOpen,
        setTrue: onOpen,
        setFalse: onClose,
    } = useBoolean()

    const formattedTimeErrors = useMemo(() => {
        return timeErrors.map(te => {
            const duration = getDuration(te)
            return {
                routingKey: { value: te.routingKey, color: getLevelColor(te.level) },
                url: { value: te.url },
                instance: { value: instances.byId[te.instance]?.name },
                service: { value: services.byId[te.service]?.name },
                requestStart: { value: getFullDate(te.requestStart), sortValue: te.requestStart },
                processStart: { value: getFullDate(te.processStart), sortValue: te.processStart },
                processEnd: { value: getFullDate(te.processEnd), sortValue: te.processEnd },
                duration: { value: shortenHumanize(duration), sortValue: duration },
                user: { value: te.user },
                module: { value: te.module },
                info: {
                    routingKey: te.routingKey,
                    instance: te.instance,
                },
                /*
                    this.referer = obj.referer // Option[String] = None,
                    this.userAgent = obj.userAgent // Option[String] = None,
                    this.body = obj.body // Option[String] = None,
                    this.size = obj.size // Option[String] = None
                */
            }
        })
    }, [instances.byId, services.byId, timeErrors])

    return (
        <>
            <CardTable
                title={'Time errors'}
                color='#161832'

                rows={formattedTimeErrors}
                headers={TABLE_HEADERS}
                onClickRow={({ info }) => {
                    setSelected(info)
                    onOpen()
                }}

                actions={[{
                    icon: 'replay',
                    onClick: reload,
                    tooltip: 'Recharger',
                }]}

                defaultSort={{ column: 'requestStart', direction: SORT.DESC }}
                status={status}
            />
            <PopinGraphTimeErrors
                isOpen={isOpen}
                onClose={onClose}
                routingKey={selected.routingKey}
                instance={selected.instance}
            />
        </>
    )
}

TimeErrorsTable.propTypes = {
    timeErrors: PropTypes.arrayOf(PropTypes.shape({
        routingKey: PropTypes.string,
        url: PropTypes.string,
        instance: PropTypes.number,
        service: PropTypes.number,
        level: PropTypes.number,
        requestStart: PropTypes.number,
        processStart: PropTypes.number,
        processEnd: PropTypes.number,
        user: PropTypes.string,
        module: PropTypes.string,
        referer: PropTypes.string,
        userAgent: PropTypes.string,
        body: PropTypes.string,
        size: PropTypes.string,
    })),
    reload: PropTypes.func,
    status: PropTypes.oneOf([STATUS.IDLE, STATUS.LOADING, STATUS.SUCCEEDED, STATUS.REJECTED]),
}

export default TimeErrorsTable